import {AllShortcutsEnabled} from '../../components/AllShortcutsEnabled'
import {useReposAnalytics} from '../../hooks/use-repos-analytics'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {SearchIcon, XCircleFillIcon} from '@primer/octicons-react'
import {TextInput} from '@primer/react'
import React from 'react'

import {clsx} from 'clsx'
import {CommandKeybindingHint} from '@github-ui/ui-commands'

type Props = {
  ariaActiveDescendant?: string
  ariaControls?: string
  ariaExpanded?: boolean
  ariaHasPopup?: boolean
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>
  onPreload(): void
  onSearch(query: string): void
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  onFocus?: React.FocusEventHandler<HTMLInputElement>
  query: string
  className?: string
  ref?: React.Ref<HTMLInputElement>
}

export const FilesSearchBox = ({
  ref,
  ariaActiveDescendant,
  ariaControls,
  ariaExpanded,
  ariaHasPopup,
  onBlur,
  onFocus,
  onKeyDown,
  onPreload,
  onSearch,
  query,
  className,
}: Props) => {
  const {sendRepoClickEvent} = useReposAnalytics()
  const [textValue, setTextValue] = React.useState(query)

  React.useEffect(() => {
    setTextValue(query)
  }, [query])

  const clearAction = query ? (
    <TextInput.Action
      onClick={() => {
        sendRepoClickEvent('FILE_TREE.CANCEL_SEARCH')
        onSearch('')
      }}
      icon={XCircleFillIcon}
      aria-label="Clear"
      className="fgColor-muted"
    />
  ) : undefined

  return (
    <TextInput
      // support for the search param can be removed once the tree is globally avaialble.
      // We want to focus the search box only if user navigates with ?search=1
      autoFocus={isSearchUrl()}
      ref={ref}
      value={textValue}
      onKeyDown={onKeyDown}
      onChange={e => {
        setTextValue(e.target.value)
        onPreload()
        onSearch(e.target.value)
      }}
      className={clsx('d-flex', className)}
      aria-label="Go to file"
      aria-activedescendant={ariaActiveDescendant}
      role={ariaHasPopup ? 'combobox' : undefined}
      aria-controls={ariaControls}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHasPopup ? 'dialog' : undefined}
      autoCorrect="off"
      spellCheck="false"
      placeholder="Go to file"
      leadingVisual={SearchIcon}
      trailingAction={clearAction}
      trailingVisual={
        clearAction
          ? undefined
          : () => (
              <AllShortcutsEnabled>
                <CommandKeybindingHint commandId="code-view:find-files" />
              </AllShortcutsEnabled>
            )
      }
      onFocus={e => {
        onPreload()
        e.target.select()
        onFocus?.(e)
      }}
      onBlur={onBlur}
      onClick={() => sendRepoClickEvent('FILE_TREE.SEARCH_BOX')}
    />
  )
}

/**
 * Whether the URL contains ?search=1 which indicates user is searching for a file
 */
export function isSearchUrl() {
  const params = new URLSearchParams(ssrSafeLocation.search)
  return params.get('search') === '1'
}

FilesSearchBox.displayName = 'FilesSearchBox'
